import React, { useEffect, useMemo, useState } from "react";
import { dataListsType } from "../shared/type";

import { Select } from "antd";
import { formatDistanceKm } from "../utils/geo";
import { getOpeningHoursStatus } from "../utils/openingHours";
import StoreCardImage from "./storeCardImage";

// type Props = {
//   props: dataListsType[];
//   selectedStoreId?: number;
//   onStoreClick?: (store: dataListsType) => void;
// };
type Props = {
  props: dataListsType[];
  /** Full prefecture/region list — shop name search matches across districts. */
  allStores?: dataListsType[];
  selectedDistrict: string;
  selectedStoreId?: number;
  onStoreClick?: (store: dataListsType) => void;
  storeDistances?: Record<number, number>;
  sortByDistance?: boolean;
};

type SearchSelection =
  | { kind: "tag"; tag: string }
  | { kind: "shop"; storeId: number; query: string }
  /** Fuzzy shop-name filter kept after leaving the search box. */
  | { kind: "query"; query: string };

const TAG_VALUE_PREFIX = "tag:";
const SHOP_VALUE_PREFIX = "shop:";
const QUERY_VALUE_PREFIX = "query:";

const splitHoursLine = (line: string) => {
  const colonIdx = line.indexOf(":");
  if (colonIdx <= 0) return { day: line, hours: line };
  return {
    day: line.slice(0, colonIdx).trim(),
    hours: line.slice(colonIdx + 1).trim(),
  };
};

const sortStores = (
  stores: dataListsType[],
  {
    sortByDistance,
    storeDistances,
  }: {
    sortByDistance: boolean;
    storeDistances?: Record<number, number>;
  },
) => {
  const sorted = [...stores];
  if (sortByDistance && storeDistances) {
    sorted.sort((a, b) => {
      const distA = storeDistances[a.id] ?? Number.POSITIVE_INFINITY;
      const distB = storeDistances[b.id] ?? Number.POSITIVE_INFINITY;
      return distA - distB;
    });
    return sorted;
  }

  return sorted.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));
};

const matchStoresByTitle = (stores: dataListsType[], keyword: string) => {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return stores.filter((store) =>
    (store.title ?? "").toLowerCase().includes(query),
  );
};

const sortMatchedStores = (
  matched: dataListsType[],
  query: string,
  preferredStoreId?: number,
) => {
  const normalized = query.trim().toLowerCase();
  return [...matched].sort((a, b) => {
    const aTitle = (a.title ?? "").toLowerCase();
    const bTitle = (b.title ?? "").toLowerCase();
    const aExact = aTitle === normalized ? 0 : 1;
    const bExact = bTitle === normalized ? 0 : 1;
    if (aExact !== bExact) return aExact - bExact;
    const aStarts = aTitle.startsWith(normalized) ? 0 : 1;
    const bStarts = bTitle.startsWith(normalized) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    if (preferredStoreId != null) {
      if (a.id === preferredStoreId) return -1;
      if (b.id === preferredStoreId) return 1;
    }
    return (a.title ?? "").localeCompare(b.title ?? "");
  });
};

const RegionalPlate: React.FC<Props> = ({
  props,
  allStores,
  selectedDistrict,
  selectedStoreId,
  onStoreClick,
  storeDistances,
  sortByDistance = false,
}) => {
  const [searchSelection, setSearchSelection] =
    useState<SearchSelection | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const searchInputRef = React.useRef(searchInput);
  const suppressQueryCommitRef = React.useRef(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [hoursOpenStoreId, setHoursOpenStoreId] = useState<number | null>(null);

  searchInputRef.current = searchInput;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setHoursOpenStoreId(null);
  }, [selectedDistrict, searchSelection, searchInput, props]);

  useEffect(() => {
    setSearchSelection(null);
    setSearchInput("");
  }, [selectedDistrict, props]);

  const areaData = useMemo(
    () => sortStores(props, { sortByDistance, storeDistances }),
    [props, sortByDistance, storeDistances],
  );

  /** Shop-name search pool: entire region (fallback to current list). */
  const regionStores = useMemo(
    () =>
      sortStores(allStores?.length ? allStores : props, {
        sortByDistance: false,
        storeDistances: undefined,
      }),
    [allStores, props],
  );

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    areaData.forEach((item) => {
      item.tags.forEach((tag) => {
        if (tag) tags.add(tag);
      });
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [areaData]);

  const shopSearchKeyword = searchInput.trim();
  const liveShopMatches = useMemo(
    () => matchStoresByTitle(regionStores, shopSearchKeyword),
    [regionStores, shopSearchKeyword],
  );

  const groupedSelectOptions = useMemo(() => {
    const keyword = shopSearchKeyword.toLowerCase();
    const matchedTags = keyword
      ? allTags.filter((tag) => tag.toLowerCase().includes(keyword))
      : allTags;

    const groups: Array<{
      label: string;
      options: Array<{ value: string; label: string }>;
    }> = [];

    if (matchedTags.length > 0) {
      groups.push({
        label: "TCG",
        options: matchedTags.map((tag) => ({
          value: `${TAG_VALUE_PREFIX}${tag}`,
          label: tag,
        })),
      });
    }

    // Shops only appear once the user has typed something.
    if (keyword) {
      const matchedShops = liveShopMatches.slice(0, 20);
      if (matchedShops.length > 0) {
        groups.push({
          label: "Shops",
          options: matchedShops.map((store) => ({
            value: `${SHOP_VALUE_PREFIX}${store.id}`,
            label: store.title,
          })),
        });
      }
    }

    return groups;
  }, [allTags, liveShopMatches, shopSearchKeyword]);

  const selectValue = useMemo(() => {
    if (!searchSelection) return undefined;
    if (searchSelection.kind === "tag") {
      return {
        value: `${TAG_VALUE_PREFIX}${searchSelection.tag}`,
        label: searchSelection.tag,
      };
    }
    if (searchSelection.kind === "query") {
      return {
        value: `${QUERY_VALUE_PREFIX}${searchSelection.query}`,
        label: searchSelection.query,
      };
    }
    return {
      value: `${SHOP_VALUE_PREFIX}${searchSelection.storeId}`,
      label: searchSelection.query,
    };
  }, [searchSelection]);

  const filteredAreaData = useMemo(() => {
    // Live shop-name filter (typing): matches across the whole region.
    // No shop matches → keep showing the current district/nearby list.
    if (shopSearchKeyword) {
      if (liveShopMatches.length > 0) {
        return sortMatchedStores(liveShopMatches, shopSearchKeyword);
      }
      return areaData;
    }

    if (!searchSelection) return areaData;

    if (searchSelection.kind === "tag") {
      return areaData.filter((item) => item.tags.includes(searchSelection.tag));
    }

    const query = searchSelection.query.trim();
    const matched = matchStoresByTitle(regionStores, query);
    if (matched.length === 0) return areaData;

    return sortMatchedStores(
      matched,
      query,
      searchSelection.kind === "shop" ? searchSelection.storeId : undefined,
    );
  }, [
    areaData,
    liveShopMatches,
    regionStores,
    searchSelection,
    shopSearchKeyword,
  ]);

  /** Keep fuzzy shop filter after leaving the box (antd clears searchValue on blur). */
  const commitShopQueryFilter = () => {
    if (suppressQueryCommitRef.current) return;
    const keyword = searchInputRef.current.trim();
    if (!keyword) return;
    setSearchSelection({ kind: "query", query: keyword });
    setSearchInput("");
  };

  const handleSearchSelect = (
    option: { value?: string | number; label?: React.ReactNode } | undefined,
  ) => {
    // Selecting an option also closes the dropdown — don't overwrite with query commit.
    suppressQueryCommitRef.current = true;
    requestAnimationFrame(() => {
      suppressQueryCommitRef.current = false;
    });

    const value = option?.value != null ? String(option.value) : undefined;
    if (!value) {
      setSearchSelection(null);
      setSearchInput("");
      return;
    }

    if (value.startsWith(TAG_VALUE_PREFIX)) {
      setSearchSelection({
        kind: "tag",
        tag: value.slice(TAG_VALUE_PREFIX.length),
      });
      setSearchInput("");
      return;
    }

    if (value.startsWith(SHOP_VALUE_PREFIX)) {
      const storeId = Number(value.slice(SHOP_VALUE_PREFIX.length));
      const store = regionStores.find((item) => item.id === storeId);
      if (!store) return;
      setSearchSelection({
        kind: "shop",
        storeId: store.id,
        query: store.title ?? "",
      });
      setSearchInput("");
      onStoreClick?.(store);
    }
  };
  return (
    <>
      <section className="w-full h-full flex flex-col md:flex-row justify-start md:justify-between items-start md:items-center md:mb-2">
        <h1 className="text-lg hidden md:block md:text-[20px] font-bold text-[#F05A2A] capitalize mb-4 md:mb-0">
          Find Your Closest TCG Shop 
        </h1>
        <div className="h-full flex justify-end items-center md:w-[250px] w-[240px]">
          <Select
            size="large"
            variant="borderless"
            virtual={false}
            className="border-[1px] border-gray-600 p-0 rounded-md"
            style={{
              width: "100%",
              outline: "none",
              backgroundColor: "#fff",
              height: ` ${windowWidth < 768 ? "32px" : "34px"}`,
            }}
            showSearch
            allowClear
            labelInValue
            value={selectValue}
            searchValue={searchInput}
            onSearch={setSearchInput}
            onChange={handleSearchSelect}
            onBlur={commitShopQueryFilter}
            onDropdownVisibleChange={(open) => {
              if (!open) commitShopQueryFilter();
            }}
            filterOption={false}
            placeholder="Search shop name or select TCG..."
            options={groupedSelectOptions}
          />
        </div>
      </section>
      <section className="w-full h-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 md:gap-x-8 gap-x-4">
        {filteredAreaData.map((item) => {
          const hoursStatus = getOpeningHoursStatus(item.openingHours);
          const hoursStatusColor =
            hoursStatus?.isOpen === true
              ? "text-[#188038]"
              : hoursStatus?.isOpen === false
                ? "text-[#d93025]"
                : "text-[#5f6368]";
          const isHoursOpen = hoursOpenStoreId === item.id;

          return (
            <article
              className={`relative w-full h-full bg-white border-[1px] rounded-3xl shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden ${
                selectedStoreId === item.id
                  ? "border-[#F05A2A] ring-2 ring-[#F05A2A]/20"
                  : "border-gray-300"
              }`}
              key={item.id}
              onClick={() => onStoreClick?.(item)}
            >
              <div className="flex flex-col justify-center items-center gap-2 font-bold p-3 min-w-0">
                <div className="w-full h-full flex flex-col justify-center items-center gap-1 shrink-0">
                  <div className="w-full h-[156px] overflow-hidden rounded-2xl bg-gray-50">
                    <StoreCardImage
                      placeId={item.placeId}
                      storeWebsite={item.storeWebsite}
                      title={item.title}
                    />
                  </div>
                </div>
                <div className="flex flex-col justify-center gap-1 min-w-0 flex-1 w-full">
                  <div className="flex items-start justify-between gap-2 w-full">
                    <b className="md:text-base text-sm text-left line-clamp-1 break-words flex-1">
                      {item.title}
                    </b>
                    {storeDistances?.[item.id] != null && (
                      <span className="shrink-0 text-xs font-semibold text-[#F05A2A] bg-[#F05A2A]/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {formatDistanceKm(storeDistances[item.id])}
                      </span>
                    )}
                  </div>

                  {item.rating != null && item.rating > 0 && (
                    <div className="flex items-center gap-1 text-xs font-medium text-black">
                      <span className="text-[#f59e0b]">★</span>
                      <span>{item.rating}</span>
                    </div>
                  )}

                  {hoursStatus && (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-xs font-normal w-full text-left appearance-none bg-transparent p-0 border-0 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHoursOpenStoreId(item.id);
                      }}
                      aria-label={`View weekly hours for ${item.title}`}
                    >
                      <span className={`font-semibold ${hoursStatusColor}`}>
                        {hoursStatus.statusLabel}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[#70757a]">
                        {hoursStatus.summaryHours}
                      </span>
                      <span className="text-[10px] text-[#70757a] shrink-0">
                        ▼
                      </span>
                    </button>
                  )}

                  <div className="flex flex-wrap items-center justify-start gap-x-[5px] gap-y-1 text-gray-700">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] text-gray-500 capitalize bg-gray-100/20 border border-gray-300 px-1 rounded-lg whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {isHoursOpen && hoursStatus ? (
                <div
                  className="absolute inset-0 z-20 rounded-3xl bg-white px-5 py-2 flex flex-col top-[120px]"
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-label={`Opening hours for ${item.title}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <div className="mt-0 flex items-center gap-1.5 text-xs">
                        <span className={`font-semibold ${hoursStatusColor}`}>
                          {hoursStatus.statusLabel}
                        </span>
                        <span className="text-[#70757a] truncate">
                          {hoursStatus.summaryHours}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 w-5 h-0 rounded-full text-gray-500 hover:bg-gray-100 text-lg leading-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHoursOpenStoreId(null);
                      }}
                      aria-label="Close opening hours"
                    >
                      ×
                    </button>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto border-t border-gray-200 py-2 space-y-1 scrollbar-hide">
                    {hoursStatus.lines.map((line) => {
                      const isToday = line === hoursStatus.todayLine;
                      const { day, hours } = splitHoursLine(line);
                      return (
                        <div
                          key={line}
                          className={`flex justify-between gap-3 text-xs leading-relaxed ${
                            isToday
                              ? "font-bold text-[#202124]"
                              : "font-normal text-[#3c4043]"
                          }`}
                        >
                          <span className="whitespace-nowrap">{day}</span>
                          <span className="text-right break-words">
                            {hours}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}
      </section>
    </>
  );
};

export default RegionalPlate;
